import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [adminProfile, setAdminProfile] = useState(null);
  const [isAdminAuthorized, setIsAdminAuthorized] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setAdminProfile(null);
        setIsAdminAuthorized(false);
        setAuthLoading(false);
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, "users", firebaseUser.uid));
        if (!userSnap.exists()) {
          await signOut(auth).catch(() => {});
          setUser(null);
          setAdminProfile(null);
          setIsAdminAuthorized(false);
          setAuthLoading(false);
          return;
        }

        const data = userSnap.data() || {};
        if (String(data.role || "").toLowerCase() !== "admin") {
          await signOut(auth).catch(() => {});
          setUser(null);
          setAdminProfile(null);
          setIsAdminAuthorized(false);
          setAuthLoading(false);
          return;
        }

        const adminSnap = await getDoc(doc(db, "admins", firebaseUser.uid)).catch(
          () => null,
        );
        if (
          adminSnap?.exists?.() &&
          String(adminSnap.data()?.status || "active").toLowerCase() !== "active"
        ) {
          await signOut(auth).catch(() => {});
          setUser(null);
          setAdminProfile(null);
          setIsAdminAuthorized(false);
          setAuthLoading(false);
          return;
        }

        setUser(firebaseUser);
        setAdminProfile(data);
        setIsAdminAuthorized(true);
      } catch (error) {
        console.error("admin auth guard failed", error);
        await signOut(auth).catch(() => {});
        setUser(null);
        setAdminProfile(null);
        setIsAdminAuthorized(false);
      } finally {
        setAuthLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, adminProfile, isAdminAuthorized, authLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
